/**
 * AI Settings API Service
 * Super Admin only - manages AI provider settings
 */

import apiClient from './client';

export type AIProvider = 'openai' | 'anthropic' | 'google' | 'xai';

export interface AIModel {
  id: number;
  provider: AIProvider;
  modelId: string;
  displayName: string;
  description?: string;
  inputPricePerMillion: number;
  outputPricePerMillion: number;
  maxTokens: number;
  supportsVision: boolean;
  supportsFunctionCalling: boolean;
  supportsStreaming: boolean;
  isActive: boolean;
  sortOrder: number;
}

export interface AIProviderSettings {
  id: string;
  tenantId: string;
  selectedProvider: AIProvider;
  selectedModel: string;
  allowedProviders: AIProvider[];
  monthlyTokenLimit: number;
  dailyTokenLimit: number;
  monthlyBudget: number;
  dailyBudget: number;
  tokensUsedToday: number;
  tokensUsedThisMonth: number;
  costToday: number;
  costThisMonth: number;
  lastUsageResetDate: string;
  lastMonthlyResetDate: string;
  createdAt: string;
  updatedAt: string;
}

export interface AIUsageSummary {
  tokensUsedToday: number;
  tokensUsedThisMonth: number;
  costToday: number;
  costThisMonth: number;
  dailyTokenLimit: number;
  monthlyTokenLimit: number;
  dailyBudget: number;
  monthlyBudget: number;
  tokensRemainingToday: number;
  tokensRemainingThisMonth: number;
  budgetRemainingToday: number;
  budgetRemainingThisMonth: number;
}

export interface GlobalAPIKeyInfo {
  provider: AIProvider;
  keyName?: string;
  isActive: boolean;
  isConfigured: boolean;
  totalTokensUsed: number;
  totalCost: number;
  lastUsedAt?: string;
}

export interface TenantAISettingsResponse {
  settings: AIProviderSettings;
  usage: AIUsageSummary;
  availableModels: AIModel[];
}

export interface GlobalAISettingsResponse {
  apiKeys: GlobalAPIKeyInfo[];
  availableModels: AIModel[];
}

export interface TenantWithAISettings {
  tenantId: string;
  tenantName: string;
  settings: AIProviderSettings;
  usage: AIUsageSummary;
}

export interface UpdateAISettingsRequest {
  selectedProvider?: AIProvider;
  selectedModel?: string;
  allowedProviders?: AIProvider[];
  monthlyTokenLimit?: number;
  dailyTokenLimit?: number;
  monthlyBudget?: number;
  dailyBudget?: number;
}

export interface SetAPIKeyRequest {
  provider: AIProvider;
  apiKey: string;
  apiSecret?: string;
  keyName?: string;
}

// API Functions

export async function getAvailableModels(): Promise<AIModel[]> {
  const response = await apiClient.get<{ success: boolean; models: AIModel[] }>(
    '/ai-settings/models'
  );
  return response.data.models;
}

export async function getGlobalSettings(): Promise<GlobalAISettingsResponse> {
  const response = await apiClient.get<{ success: boolean } & GlobalAISettingsResponse>(
    '/ai-settings/global'
  );
  return {
    apiKeys: response.data.apiKeys,
    availableModels: response.data.availableModels,
  };
}

export async function setGlobalAPIKey(request: SetAPIKeyRequest): Promise<void> {
  await apiClient.put('/ai-settings/global/api-key', request);
}

export async function deleteGlobalAPIKey(provider: AIProvider): Promise<void> {
  await apiClient.delete(`/ai-settings/global/api-key/${provider}`);
}

export async function validateAPIKey(
  provider: AIProvider,
  apiKey: string
): Promise<{ valid: boolean; error?: string }> {
  const response = await apiClient.post<{ success: boolean; valid: boolean; error?: string }>(
    '/ai-settings/validate-key',
    { provider, apiKey }
  );
  return { valid: response.data.valid, error: response.data.error };
}

export async function getAllTenantsAISettings(): Promise<TenantWithAISettings[]> {
  const response = await apiClient.get<{ success: boolean; tenants: TenantWithAISettings[] }>(
    '/ai-settings/tenants'
  );
  return response.data.tenants;
}

export async function getTenantAISettings(tenantId: string): Promise<TenantAISettingsResponse> {
  const response = await apiClient.get<{ success: boolean } & TenantAISettingsResponse>(
    `/ai-settings/tenants/${tenantId}`
  );
  return {
    settings: response.data.settings,
    usage: response.data.usage,
    availableModels: response.data.availableModels,
  };
}

export async function updateTenantAISettings(
  tenantId: string,
  updates: UpdateAISettingsRequest
): Promise<{ settings: AIProviderSettings; usage: AIUsageSummary }> {
  const response = await apiClient.put<{
    success: boolean;
    settings: AIProviderSettings;
    usage: AIUsageSummary;
  }>(`/ai-settings/tenants/${tenantId}`, updates);
  return { settings: response.data.settings, usage: response.data.usage };
}

// Helper functions

export const PROVIDER_DISPLAY_NAMES: Record<AIProvider, string> = {
  openai: 'OpenAI',
  anthropic: 'Anthropic',
  google: 'Google AI',
  xai: 'xAI (Grok)',
};

export const PROVIDER_COLORS: Record<AIProvider, string> = {
  openai: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  anthropic: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
  google: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  xai: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
};

export function formatTokens(tokens: number): string {
  if (tokens === -1) return 'Unlimited';
  if (tokens >= 1000000) return `${(tokens / 1000000).toFixed(1)}M`;
  if (tokens >= 1000) return `${(tokens / 1000).toFixed(1)}K`;
  return tokens.toString();
}

export function formatCost(cost: number): string {
  return `$${cost.toFixed(2)}`;
}

export function getUsagePercentage(used: number, limit: number): number {
  if (limit === -1) return 0;
  return Math.min(100, (used / limit) * 100);
}
